import React, { Suspense, lazy, useMemo, useState } from 'react'
import { Box } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { RoleManager } from '../../../APIManager'
import { type RoleSummary } from '../../../APIManager/RoleManager'
import GenericButton from '../../../components/GenericButton'
import { ButtonSize, ButtonType } from '../../../components/GenericButton/styles'
import { notification } from '../../../components/Notifications'
import { type ApiError } from '../../../models/Common'
import { type ApplyFiltersAndDispatchParams } from '../../../components/CustomTable/types'
import LoadingPage from '../../../pages/LoadingPage'
import DeleteViewerRole from './DeleteViewerRole'
import {
  buildViewerRolesList,
  filterViewerRolesBySearch,
  paginateViewerRolesList
} from './utils'

const AppTable = lazy(async () => await import('../../../components/CustomTable'))

const VIEWER_ROLES_QUERY_KEY = ['viewerFamilyRoles']
const TABLE_TITLE = 'Report Viewer Roles Management'

const ReportViewerRolesTab: React.FC = () => {
  const queryClient = useQueryClient()
  const [params, setParams] = useState<ApplyFiltersAndDispatchParams>({
    search: '',
    pageSize: 10,
    pageNumber: 1
  })

  const { data: roles = [], isLoading } = useQuery({
    queryKey: VIEWER_ROLES_QUERY_KEY,
    queryFn: async () => {
      const { data } = await RoleManager.getViewerFamilyRoles()
      return data
    }
  })

  const list = useMemo(() => {
    const filtered = filterViewerRolesBySearch(roles, params.search ?? '')
    const baseList = buildViewerRolesList(filtered)
    const pageSize = params.pageSize ?? 10

    // Same shape as RoleTable's server-paginated CDG: when everything fits on
    // one page, pass the full list and let pagination.pageSize match row count.
    if (baseList.data.length <= pageSize) {
      return baseList
    }

    return paginateViewerRolesList(
      baseList,
      params.pageNumber ?? 1,
      pageSize
    )
  }, [roles, params.search, params.pageNumber, params.pageSize])

  const appendViewerRole = (role: RoleSummary): void => {
    queryClient.setQueryData<RoleSummary[]>(VIEWER_ROLES_QUERY_KEY, (prev = []) => {
      if (prev.some((existing) => existing.roleId === role.roleId)) {
        return prev
      }
      return [...prev, role]
    })
  }

  const removeViewerRole = (roleId: string): void => {
    queryClient.setQueryData<RoleSummary[]>(VIEWER_ROLES_QUERY_KEY, (prev = []) =>
      prev.filter((role) => role.roleId !== roleId)
    )
  }

  const { mutate: copyRole, isPending: isCopyPending } = useMutation({
    mutationFn: async () => {
      const { data } = await RoleManager.copyViewerRole()
      return data
    },
    onSuccess: (newRole) => {
      appendViewerRole(newRole)
      notification('Viewer role created', { variant: 'success' })
    },
    onError: (error) => {
      const customError = error as ApiError
      notification(customError?.message ?? 'Failed to create viewer role', { variant: 'error' })
    }
  })

  const handleSearch = (value: string): void => {
    setParams((prev) => ({ ...prev, search: value, pageNumber: 1 }))
  }

  const handlePageChange = (page: number): void => {
    setParams((prev) => ({ ...prev, pageNumber: page }))
  }

  const handleRowsPerPageChange = (pageSize: number): void => {
    setParams((prev) => ({ ...prev, pageSize, pageNumber: 1 }))
  }

  const extraModalProps = {
    width: 'sm'
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Suspense fallback={<LoadingPage />}>
        <AppTable
          topbarTitle={TABLE_TITLE}
          list={list}
          rows={list?.data}
          loading={isLoading}
          featureName='Viewer Role'
          hideEditColumn
          disableRowDoubleClick
          enableAllSelection={false}
          deleteAction={
            <DeleteViewerRole onDeleted={removeViewerRole} />
          }
          actionModalEntityDisplayField='roleName'
          extraModalProps={extraModalProps}
          params={params}
          setParams={setParams}
          onSearch={handleSearch}
          handlePageChange={handlePageChange}
          handleRowsPerPageChange={handleRowsPerPageChange}
          customTopbarAction={
            <GenericButton
              label='Add Viewer Role'
              buttonType={ButtonType.Primary}
              buttonSize={ButtonSize.Medium}
              fontWeight='bold'
              loading={isCopyPending}
              disabled={isLoading || isCopyPending}
              onClick={() => { copyRole() }}
            />
          }
        />
      </Suspense>
    </Box>
  )
}

export default ReportViewerRolesTab
